import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  MessageSquare, Plus, Send, ArrowLeft, Pin, Lock, Eye, Loader2,
  ChevronRight, Users,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/wingman/7b8b7474-e52e-4388-adc0-a2361520c0a4/attachments/8840390a845949e6b9092405676c1743_image.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "general", label: "Generale", color: "text-[#C0C0C0]" },
  { value: "nutrition", label: "Nutrizione", color: "text-emerald-300" },
  { value: "training", label: "Allenamento", color: "text-amber-300" },
  { value: "recipes", label: "Ricette", color: "text-sky-300" },
  { value: "reviews", label: "Recensioni Prodotti", color: "text-pink-300" },
];

const ForumShell = ({ children }) => (
  <div className="min-h-screen bg-[#060A14] text-white" data-testid="forum-page">
    <header className="border-b border-white/10 bg-[#060A14]/95 backdrop-blur-md sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-3">
          <img src={LOGO_URL} alt="LEOVERA" className="h-9 w-auto bg-white rounded-md p-1" />
          <span className="text-[10px] tracking-[0.32em] uppercase text-[#94A3B8]">Community</span>
        </Link>
        <Link to="/" data-testid="forum-back-home" className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" />
          Torna al sito
        </Link>
      </div>
    </header>
    {children}
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso.slice(0, 16); }
};

export const ForumIndex = () => {
  const { user, userApi } = useAuth();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "general" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const fetchTopics = async (cat) => {
    setLoading(true);
    try {
      const url = `${API}/forum/topics${cat ? `?category=${cat}` : ""}`;
      const { data } = await axios.get(url);
      setTopics(data || []);
    } catch { setTopics([]); }
    setLoading(false);
  };

  useEffect(() => { fetchTopics(category); }, [category]);

  const submitTopic = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login", { state: { from: "/forum" } });
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await userApi.post(`/forum/topics`, form);
      setCreating(false);
      setForm({ title: "", body: "", category: "general" });
      navigate(`/forum/${data.slug}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore durante la creazione");
    } finally { setBusy(false); }
  };

  return (
    <ForumShell>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Hero */}
        <section className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          <div>
            <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.36em] uppercase text-[#C0C0C0]">
              <Users className="w-3.5 h-3.5" /> Community LEOVERA
            </span>
            <h1 className="mt-3 font-[Outfit] text-white text-3xl sm:text-5xl font-light tracking-tight">
              Il forum degli atleti.
            </h1>
            <p className="mt-3 text-[#94A3B8] text-sm max-w-xl">
              Discuti integrazione, allenamento, ricette e recensioni con la community di LEOVERA.
            </p>
          </div>
          <button onClick={() => (user ? setCreating(true) : navigate("/login", { state: { from: "/forum" } }))}
                  data-testid="forum-new-topic-btn"
                  className="inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-5 py-3 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white">
            <Plus className="w-4 h-4" /> Nuova discussione
          </button>
        </section>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="forum-categories">
          <button onClick={() => setCategory("")}
                  data-testid="cat-all"
                  className={`px-3 py-1.5 rounded-full text-[10px] tracking-[0.22em] uppercase border ${
                    category === "" ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : "border-white/15 text-[#94A3B8] hover:text-white"
                  }`}>Tutti</button>
          {CATEGORIES.map((c) => (
            <button key={c.value} onClick={() => setCategory(c.value)}
                    data-testid={`cat-${c.value}`}
                    className={`px-3 py-1.5 rounded-full text-[10px] tracking-[0.22em] uppercase border transition-colors ${
                      category === c.value ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : "border-white/15 text-[#94A3B8] hover:text-white"
                    }`}>{c.label}</button>
          ))}
        </div>

        {/* New topic form */}
        {creating && (
          <form onSubmit={submitTopic} data-testid="forum-create-form" className="mb-8 bg-[#0A1121] border border-[#C0C0C0]/30 rounded-md p-6 space-y-4">
            <h3 className="font-[Outfit] text-white text-lg tracking-tight">Nuova discussione</h3>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="forum-new-category"
                    className="w-full bg-[#060A14] border border-white/10 rounded-sm px-3 py-2.5 text-white text-sm">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input required minLength={4} maxLength={140} value={form.title}
                   data-testid="forum-new-title"
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="Titolo discussione (min. 4 caratteri)…"
                   className="w-full bg-[#060A14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
            <textarea required minLength={10} maxLength={4000} rows={5} value={form.body}
                      data-testid="forum-new-body"
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      placeholder="Cosa vuoi condividere o chiedere?"
                      className="w-full resize-none bg-[#060A14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
            {error && <div data-testid="forum-create-error" className="px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 text-[12px]">{error}</div>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setCreating(false)} className="px-5 py-2.5 rounded-sm text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] hover:text-white">Annulla</button>
              <button type="submit" disabled={busy} data-testid="forum-create-submit"
                      className="inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-5 py-2.5 rounded-sm text-[10px] tracking-[0.28em] uppercase font-medium hover:bg-white disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Pubblica
              </button>
            </div>
          </form>
        )}

        {/* Topics list */}
        <section className="bg-[#0A1121] border border-white/10 rounded-md overflow-hidden" data-testid="forum-topics-list">
          {loading ? (
            <div className="p-10 text-center text-[#94A3B8] text-sm tracking-[0.28em] uppercase">Caricamento…</div>
          ) : topics.length === 0 ? (
            <div className="p-10 text-center" data-testid="forum-empty">
              <MessageSquare className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" strokeWidth={1.2} />
              <p className="text-[#E5E4E2] text-sm">Nessuna discussione ancora.</p>
              <p className="text-[#94A3B8] text-[11px] mt-1">Sii il primo ad avviarne una!</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {topics.map((t) => {
                const cat = CATEGORIES.find((c) => c.value === t.category) || CATEGORIES[0];
                return (
                  <Link key={t.id} to={`/forum/${t.slug}`} data-testid={`topic-row-${t.id}`}
                        className="flex items-center gap-4 p-4 sm:p-5 hover:bg-white/[0.02] transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {t.pinned && <Pin className="w-3 h-3 text-amber-300" />}
                        {t.locked && <Lock className="w-3 h-3 text-red-300" />}
                        <span className={`text-[9px] tracking-[0.28em] uppercase ${cat.color}`}>{cat.label}</span>
                      </div>
                      <h3 className="mt-1.5 font-[Outfit] text-white text-base tracking-tight truncate">{t.title}</h3>
                      <p className="mt-1 text-[11px] text-[#94A3B8]">
                        <span className="text-[#E5E4E2]">{t.author_name}</span> · {formatDate(t.created_at)}
                      </p>
                    </div>
                    <div className="hidden sm:flex flex-col items-end gap-1 text-[11px] text-[#94A3B8] shrink-0">
                      <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.reply_count || 0}</span>
                      <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {t.views || 0}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </ForumShell>
  );
};

export const ForumTopic = () => {
  const { slug } = useParams();
  const { user, userApi } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchTopic = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/forum/topics/${slug}`);
      setTopic(data.topic);
      setReplies(data.replies || []);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchTopic(); }, [slug]);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login", { state: { from: `/forum/${slug}` } }); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await userApi.post(`/forum/topics/${topic.id}/replies`, { body: reply });
      setReplies((r) => [...r, data]);
      setReply("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <ForumShell>
        <main className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h2 className="font-[Outfit] text-white text-2xl">Discussione non trovata</h2>
          <Link to="/forum" className="mt-6 inline-flex items-center gap-2 text-[#C0C0C0] hover:text-white text-[11px] tracking-[0.28em] uppercase">
            <ArrowLeft className="w-3.5 h-3.5" /> Torna al forum
          </Link>
        </main>
      </ForumShell>
    );
  }

  if (loading || !topic) {
    return (
      <ForumShell>
        <main className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Loader2 className="w-6 h-6 text-[#C0C0C0] animate-spin mx-auto" />
        </main>
      </ForumShell>
    );
  }

  const cat = CATEGORIES.find((c) => c.value === topic.category) || CATEGORIES[0];

  return (
    <ForumShell>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/forum" data-testid="topic-back" className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] hover:text-white mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Tutte le discussioni
        </Link>

        {/* Original post */}
        <article data-testid="topic-article" className="bg-[#0A1121] border border-white/10 rounded-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            {topic.pinned && <Pin className="w-3 h-3 text-amber-300" />}
            {topic.locked && <Lock className="w-3 h-3 text-red-300" />}
            <span className={`text-[10px] tracking-[0.28em] uppercase ${cat.color}`}>{cat.label}</span>
          </div>
          <h1 data-testid="topic-title" className="font-[Outfit] text-white text-2xl sm:text-3xl tracking-tight font-light leading-tight">
            {topic.title}
          </h1>
          <p className="mt-3 text-[11px] text-[#94A3B8]">
            <span className="text-[#E5E4E2]">{topic.author_name}</span> · {formatDate(topic.created_at)}
          </p>
          <p className="mt-5 text-[#E5E4E2] text-[14px] leading-relaxed whitespace-pre-wrap font-[Manrope]">
            {topic.body}
          </p>
        </article>

        {/* Replies */}
        <section data-testid="replies-list" className="space-y-3">
          {replies.map((r) => (
            <div key={r.id} data-testid={`reply-${r.id}`} className="bg-[#0A1121] border border-white/10 rounded-md p-5">
              <p className="text-[11px] text-[#94A3B8] mb-2">
                <span className="text-[#E5E4E2]">{r.author_name}</span> · {formatDate(r.created_at)}
              </p>
              <p className="text-[#E5E4E2] text-[14px] leading-relaxed whitespace-pre-wrap font-[Manrope]">{r.body}</p>
            </div>
          ))}
        </section>

        {/* Reply form */}
        {topic.locked ? (
          <div className="mt-8 p-5 rounded-md bg-red-500/10 border border-red-500/30 text-red-200 text-[12px] flex items-center gap-2">
            <Lock className="w-4 h-4" /> Questa discussione è chiusa. Nuove risposte non sono permesse.
          </div>
        ) : !user ? (
          <div className="mt-8 p-5 rounded-md bg-[#0A1121] border border-white/10 text-center">
            <p className="text-[#94A3B8] text-sm">Accedi per partecipare alla discussione.</p>
            <Link to="/login" state={{ from: `/forum/${slug}` }} data-testid="topic-login-cta"
                  className="mt-3 inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-5 py-2.5 rounded-sm text-[10px] tracking-[0.28em] uppercase font-medium hover:bg-white">
              Accedi per rispondere
            </Link>
          </div>
        ) : (
          <form onSubmit={submitReply} data-testid="reply-form" className="mt-8 bg-[#0A1121] border border-white/10 rounded-md p-5 space-y-3">
            <textarea required minLength={2} maxLength={4000} rows={4} value={reply}
                      data-testid="reply-input"
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Scrivi una risposta…"
                      className="w-full resize-none bg-[#060A14] border border-white/10 rounded-sm px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
            {error && <div className="text-[12px] text-red-300">{error}</div>}
            <div className="flex justify-end">
              <button type="submit" disabled={busy} data-testid="reply-submit"
                      className="inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-5 py-2.5 rounded-sm text-[10px] tracking-[0.28em] uppercase font-medium hover:bg-white disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Pubblica risposta
              </button>
            </div>
          </form>
        )}
      </main>
    </ForumShell>
  );
};
